import {
    Calculation,
    PaymentFrequency,
    PaymentStructure,
} from '@servicetitan/financing-api-client';
import { PlanCard } from './PlanCard';
import { Flex, Grid, Text } from '@servicetitan/anvil2';
import { plansListGrid, legalDisclosure, plansContainer } from './PlansList.module.scss';
import {
    SetupDataStore,
    useProviderConfig,
    SecondLookApplicationSubmittedStore,
} from '@servicetitan/financing-workflow-shared';
import { useLegalDisclosure } from '@servicetitan/financing-utils-shared';
import { ReactNode, useState, useMemo, useRef } from 'react';
import { PaymentFrequencyTabs } from './PaymentFrequencyTabs';
import { v4 as uuidv4 } from 'uuid';

// Filter plans by frequency - moved outside component for performance
const filterPlansByFrequency = (frequency: PaymentFrequency, plansToFilter: Calculation[]) => {
    return plansToFilter.filter(plan => {
        // If no paymentFrequency field exists, show all plans for the 'weekly' frequency to maintain compatibility
        if (!plan.paymentFrequency) {
            return frequency === PaymentFrequency.Weekly;
        }

        // Match frequency values case-insensitively to handle different casing from API
        return plan.paymentFrequency.toLowerCase() === frequency.toLowerCase();
    });
};

const deduplicatePlans = (plans: Calculation[]): Calculation[] => {
    const seen = new Set<string>();
    return plans.filter(plan => {
        if (!plan.planNumber || seen.has(plan.planNumber)) {
            return false;
        }
        seen.add(plan.planNumber);
        return true;
    });
};

export interface PlansListProps {
    plans: Calculation[];
    instructions?: string | ReactNode;
    onPlanSelected?: (plan: Calculation) => void;
    disableSelection?: boolean;
}

export const PlansList = ({
    plans,
    instructions,
    onPlanSelected,
    disableSelection = false,
}: PlansListProps) => {
    const { setupData } = SetupDataStore.useStore();
    const {
        legalDisclosureCategory,
        customLegalDisclosureText,
        customLegalDisclosureTextForDeferredInterest,
        enablePaymentFrequencyTabs,
    } = useProviderConfig();
    const { isSecondLookApplicationSubmitted } = SecondLookApplicationSubmittedStore.useStore();
    const legalDisclosureResult = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: legalDisclosureCategory,
    });

    const [currentFrequency, setCurrentFrequency] = useState<PaymentFrequency>(
        PaymentFrequency.Weekly,
    );

    // Ref for the grid container to control scroll position
    const gridRef = useRef<HTMLDivElement>(null);

    // Handle frequency change from payment frequency tabs
    const handleFrequencyChange = (frequency: PaymentFrequency) => {
        setCurrentFrequency(frequency);
    };

    const uniquePlans = useMemo(() => deduplicatePlans(plans), [plans]);

    // Derive displayed plans using useMemo - no separate state needed
    const plansToShow = useMemo(() => {
        if (!enablePaymentFrequencyTabs) {
            return uniquePlans;
        }
        return filterPlansByFrequency(currentFrequency, uniquePlans);
    }, [uniquePlans, currentFrequency, enablePaymentFrequencyTabs]);

    // Generate stable UUID keys for each plan object - only when plans change, not on tab switch
    const planKeys = useMemo(() => {
        const keyMap = new Map<Calculation, string>();
        plans.forEach(plan => {
            keyMap.set(plan, uuidv4());
        });
        return keyMap;
    }, [plans]);

    // Check if any plan has deferred interest
    const hasDeferredInterestPlan = useMemo(() => {
        return plansToShow.some(
            plan =>
                plan.paymentStructure === PaymentStructure.DeferredInterest ||
                plan.paymentStructure === PaymentStructure.DeferredInterestAndPrincipal,
        );
    }, [plansToShow]);

    // Show custom disclosure for deferred interest plans only when:
    // 1. The config has a deferred interest disclosure text
    // 2. There are deferred interest plans in the list
    // 3. The second look application has been submitted (meaning we're showing Turns plans)
    const shouldShowDeferredInterestDisclosure =
        customLegalDisclosureTextForDeferredInterest != null &&
        hasDeferredInterestPlan &&
        isSecondLookApplicationSubmitted;

    // Show general custom disclosure (if not using deferred interest specific one)
    const shouldShowCustomDisclosure =
        customLegalDisclosureText != null && customLegalDisclosureTextForDeferredInterest == null;

    // Handle the legal disclosure text, which may be string or string[]
    // Join arrays with line breaks to preserve formatting
    const formattedLegalDisclosureText = Array.isArray(legalDisclosureResult)
        ? legalDisclosureResult.join('<br />')
        : legalDisclosureResult;

    return (
        <Flex direction="column" gap="4" className={plansContainer}>
            {enablePaymentFrequencyTabs && (
                <PaymentFrequencyTabs
                    plans={plans}
                    onFrequencyChange={handleFrequencyChange}
                    scrollContainerRef={gridRef}
                />
            )}

            <Text variant="headline" el="h2">
                Select a Plan
            </Text>
            {instructions && (
                <Text variant="body" subdued size="small" style={{ marginBottom: '16px' }}>
                    {instructions}
                </Text>
            )}

            {plansToShow.length > 0 ? (
                <Grid ref={gridRef} className={plansListGrid} role="list">
                    {plansToShow.map(plan => (
                        <PlanCard
                            key={planKeys.get(plan)}
                            plan={plan}
                            onPlanSelected={onPlanSelected}
                            showLowestPayment={plansToShow.length > 1}
                            currentFrequency={
                                enablePaymentFrequencyTabs ? currentFrequency : undefined
                            }
                            disableSelection={disableSelection}
                        />
                    ))}
                </Grid>
            ) : enablePaymentFrequencyTabs ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    style={{
                        padding: '32px 16px',
                        backgroundColor: 'var(--color-background-subtle)',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border-subdued)',
                    }}
                >
                    <Text
                        variant="body"
                        style={{ color: 'var(--color-text-subdued)', textAlign: 'center' }}
                    >
                        No plans available to display.
                    </Text>
                </Flex>
            ) : null}

            {/* Custom legal disclosure text for deferred interest plans */}
            {shouldShowDeferredInterestDisclosure && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: customLegalDisclosureTextForDeferredInterest,
                    }}
                />
            )}

            {/* General custom legal disclosure text */}
            {shouldShowCustomDisclosure && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: customLegalDisclosureText,
                    }}
                />
            )}

            {/* Standard legal disclosure text */}
            {formattedLegalDisclosureText !== '' && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    className={legalDisclosure}
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: formattedLegalDisclosureText,
                    }}
                />
            )}
        </Flex>
    );
};
